import { useMemo } from "react"

import { useListTestCasesTransport } from "@/test-set/transport"
import { FileText } from "lucide-react"

import { FilterPopover } from "@/components/filters/FilterPopover"
import { isFilterSectionDisabled } from "@/components/filters/filterSectionVisibility"
import { FilterTrigger } from "@/components/filters/FilterTrigger"
import { SearchableCheckboxList } from "@/components/filters/SearchableCheckboxList"

import { useRunsFilter } from "../../contexts/RunsFilterContext"
import type { FilterSectionProps } from "../../types/filters"

export const TestCaseFilterSection: React.FC<FilterSectionProps> = ({
  isOpen,
  onToggle,
  filterEnabled,
}) => {
  const { testCaseFilters, setTestCaseFilters } = useRunsFilter()
  const {
    data: testCases,
    isError,
    isLoading,
  } = useListTestCasesTransport({
    enabled: filterEnabled,
  })

  const items = useMemo(() => {
    if (!testCases) {
      return []
    }
    return testCases.map((tc) => {
      const truncatedInput =
        tc.input_str.length > 60
          ? `${tc.input_str.slice(0, 60)}...`
          : tc.input_str
      const label = tc.description || truncatedInput
      return {
        id: tc.id,
        label: `${tc.test_set_name}: ${label}`,
        searchValue: `${tc.test_set_name} ${tc.description || ""} ${tc.input_str}`,
      }
    })
  }, [testCases])

  const isDisabled = isFilterSectionDisabled({
    activeFilterCount: testCaseFilters.length,
    isDeferred: filterEnabled === false,
    isError,
    isLoading,
    optionCount: items.length,
  })

  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={isDisabled}
      emptyMessage="No test cases available in this workspace."
      trigger={
        <FilterTrigger
          label="Test Cases"
          icon={<FileText className="w-4 h-4" />}
          isActive={testCaseFilters.length > 0}
          disabled={isDisabled}
        />
      }
    >
      <SearchableCheckboxList
        items={items}
        selectedIds={testCaseFilters}
        onSelectionChange={setTestCaseFilters}
        isLoading={isLoading}
        isDeferred={filterEnabled === false}
        isOpen={isOpen}
        placeholder="Search test cases..."
        emptyMessage="No test cases found"
        noItemsMessage="No test cases available"
      />
    </FilterPopover>
  )
}
